import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaFileSignature, FaPlus } from 'react-icons/fa';
import InfoCard from '../../components/shared/InfoCard';
import { ActionButton, StatusBadge } from '../../components/styled/CommonStyles';
import { formatCurrency } from '../../utils/utils';
import ChangeOrderFormModal from './ChangeOrderFormModal';
import ChangeOrderDetailModal from './ChangeOrderDetailModal';

const STATUS_COLORS = {
  Draft: '#6c757d',
  Sent: '#007bff',
  Viewed: '#17a2b8',
  Approved: '#28a745',
  Declined: '#dc3545',
  Cancelled: '#6c757d',
};

const CO_STATUSES = ['Accepted', 'Completed'];

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(1)};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing(2)};
  padding: ${(props) => props.theme.spacing(2)};
  background: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  cursor: pointer;
  border-left: 3px solid ${(props) => props.accent || props.theme.colors.primary};
  transition: transform 0.15s ease;

  &:hover { transform: translateX(2px); }

  .co-title {
    font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  }

  .co-meta {
    font-size: 12px;
    color: ${(props) => props.theme.colors.textSecondary};
  }

  .co-right {
    display: flex;
    align-items: center;
    gap: ${(props) => props.theme.spacing(2)};
    flex-shrink: 0;
  }
`;

const Empty = styled.div`
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.9em;
  padding: ${(props) => props.theme.spacing(2)} 0;
`;

const Breakdown = styled.div`
  margin-top: ${(props) => props.theme.spacing(3)};
  padding-top: ${(props) => props.theme.spacing(2)};
  border-top: 1px dashed ${(props) => props.theme.colors.inputGray}66;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .line {
    display: flex;
    justify-content: space-between;
    font-size: 0.95em;
    color: ${(props) => props.theme.colors.textSecondary};
  }

  .total {
    display: flex;
    justify-content: space-between;
    margin-top: ${(props) => props.theme.spacing(1)};
    padding-top: ${(props) => props.theme.spacing(1)};
    border-top: 1px solid ${(props) => props.theme.colors.inputGray}33;
    font-weight: bold;
    font-size: 1.05em;
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

const BadgeWrapper = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: ${(props) => props.theme.colors.primary}22;
  color: ${(props) => props.theme.colors.primary};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  vertical-align: middle;
`;

export const ChangeOrderSummaryBadge = ({ changeOrders }) => {
  if (!changeOrders || changeOrders.length === 0) return null;
  const approved = changeOrders.filter((co) => co.status === 'Approved');
  if (approved.length === 0) return null;
  const total = approved.reduce((sum, co) => sum + (parseFloat(co.totalPrice) || 0), 0);
  return (
    <BadgeWrapper>
      +{approved.length} {approved.length === 1 ? 'change order' : 'change orders'} ({formatCurrency(total)})
    </BadgeWrapper>
  );
};

const ChangeOrderListSection = ({
  estimate,
  estimateId,
  estimateStatus,
  changeOrders = [],
  services = [],
  onChange,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const sorted = useMemo(() => {
    return [...changeOrders].sort((a, b) => {
      const da = new Date(a.createdAt || 0).getTime();
      const db = new Date(b.createdAt || 0).getTime();
      return da - db;
    });
  }, [changeOrders]);

  const sequenceById = useMemo(() => {
    const map = {};
    sorted.forEach((co, idx) => { map[co.id] = idx + 1; });
    return map;
  }, [sorted]);

  if (!CO_STATUSES.includes(estimateStatus)) return null;

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEditFromDetail = (co) => {
    setDetailId(null);
    setEditing(co);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setFormOpen(false);
    setEditing(null);
    onChange?.();
  };

  const handleApproved = () => {
    onChange?.();
  };

  const approved = changeOrders.filter((co) => co.status === 'Approved');
  const originalTotal = estimate?.originalTotalPrice != null
    ? parseFloat(estimate.originalTotalPrice)
    : null;
  const currentTotal = estimate?.totalPrice != null ? parseFloat(estimate.totalPrice) : null;
  const showBreakdown =
    originalTotal != null &&
    currentTotal != null &&
    approved.length > 0 &&
    Math.abs(originalTotal - currentTotal) > 0.001;

  return (
    <>
      <InfoCard
        title="Change Orders"
        icon={<FaFileSignature />}
        actions={
          <ActionButton type="button" onClick={handleCreate}>
            <FaPlus /> Create Change Order
          </ActionButton>
        }
      >
        {sorted.length === 0 ? (
          <Empty>No change orders yet.</Empty>
        ) : (
          <List>
            {sorted.map((co) => {
              const seq = sequenceById[co.id];
              return (
                <Row
                  key={co.id}
                  accent={STATUS_COLORS[co.status]}
                  onClick={() => setDetailId(co.id)}
                >
                  <div>
                    <div className="co-title">CO #{seq} — {co.title}{co.kind === 'Credit' && <BadgeWrapper>Credit</BadgeWrapper>}</div>
                    <div className="co-meta">
                      {co.approvedAt && `Approved ${new Date(co.approvedAt).toLocaleDateString()}`}
                      {!co.approvedAt && co.createdAt && `Created ${new Date(co.createdAt).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="co-right">
                    <strong>{formatCurrency(co.totalPrice)}</strong>
                    <StatusBadge color={STATUS_COLORS[co.status] || '#6c757d'}>{co.status}</StatusBadge>
                  </div>
                </Row>
              );
            })}
          </List>
        )}

        {showBreakdown && (
          <Breakdown>
            <div className="line">
              <span>Original quote</span>
              <span>{formatCurrency(originalTotal)}</span>
            </div>
            {approved.map((co) => (
              <div className="line" key={co.id}>
                <span>{co.kind === 'Credit' ? '− Credit' : '+ Change Order'} #{sequenceById[co.id]}</span>
                <span>{formatCurrency(co.totalPrice)}</span>
              </div>
            ))}
            <div className="total">
              <span>Total</span>
              <span>{formatCurrency(currentTotal)}</span>
            </div>
          </Breakdown>
        )}
      </InfoCard>

      <ChangeOrderFormModal
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={handleSaved}
        estimateId={estimateId}
        estimate={estimate}
        services={services}
        existing={editing}
      />

      <ChangeOrderDetailModal
        isOpen={detailId != null}
        changeOrderId={detailId}
        sequenceLabel={detailId ? `CO #${sequenceById[detailId]}` : null}
        onClose={() => setDetailId(null)}
        onEdit={handleEditFromDetail}
        onChanged={onChange}
        onApproved={handleApproved}
      />
    </>
  );
};

export default ChangeOrderListSection;
